import { Select, SelectOption } from '@acp/core-ui';
import React, { useMemo } from 'react';
import { Props } from 'chakra-react-select';
import { CancellationReasonGroup } from '@acp/shared';
import { useQuery } from '@tanstack/react-query';
import { getCancellationReasons } from '../../service';

type CancellationReasonOption = SelectOption<number>;
type GroupedCancellationReasonOption = {
    label: string;
    options: CancellationReasonOption[];
};

function CancellationReasonSelect({
    value,
    onChange,
    size,
}: {
    onChange: Props<CancellationReasonOption, false>['onChange'];
    value: number | null;
    size?: React.ComponentProps<typeof Select>['size'];
}) {
    const query = useQuery({
        queryKey: ['property', 'cancellation-reasons'],
        queryFn: () => getCancellationReasons(),
    });

    const groupedOptions = useMemo(() => {
        if (query.isLoading) {
            return [];
        }

        const reasons: CancellationReasonGroup[] = query.data ?? [];

        return reasons.map((group) => ({
            label: group.text,
            options: group.children.map((reason) => ({
                label: reason.text,
                value: reason.id,
            })),
        }));
    }, [query.data, query.isLoading]);

    const options = useMemo(
        () => groupedOptions.flatMap((reason) => reason.options),
        [groupedOptions]
    );

    const selectedOption = useMemo(
        () => options.find((option) => option.value === value),
        [options, value]
    );

    return (
        <Select<CancellationReasonOption, false, GroupedCancellationReasonOption>
            options={groupedOptions}
            onChange={onChange}
            value={selectedOption}
            isLoading={query.isLoading}
            placeholder="Select a cancellation reason"
            size={size}
        />
    );
}

export { CancellationReasonSelect };
